import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardFilterBar } from '@/features/dashboard-filters';
import { useHeaderFilters } from '@/features/header-filters';

import { useMappingFilters } from '../lib/use-mapping-filters';
import { MappingPage } from './mapping-page';

/**
 * Hands the map's filters to the shared header panel, the way every other filterable
 * page does, so the page itself is nothing but the map. Filters apply as they change
 * — the panel's Apply only closes it, since there is no draft state to commit.
 */
export function MappingWithFilters() {
  const { t } = useTranslation();
  const { setFilterConfig } = useHeaderFilters();
  const filters = useMappingFilters();

  // Every dependency here is an individual value the hook memoises. Depending on the
  // hook's return object instead would loop forever: it is a fresh literal per render,
  // and publishing the config re-renders this component.
  const {
    consignmentNumber,
    setConsignmentNumber,
    organizationOptions,
    selectedOrganization,
    setSelectedOrganization,
    setOrganizationSearch,
    isLoadingOrganizations,
    vehicleCategoryOptions,
    selectedVehicleCategories,
    setSelectedVehicleCategories,
    activeFilters,
    activeFiltersCount,
    hasActiveFilters,
    resetFilters
  } = filters;

  useEffect(() => {
    setFilterConfig({
      id: 'mapping-filters',
      title: t('welcome.mapping.label'),
      content: (
        <DashboardFilterBar
          variant="mapping"
          options={{}}
          onOptionChange={() => {}}
          consignmentNumber={consignmentNumber}
          onConsignmentNumberChange={setConsignmentNumber}
          organizationOptions={organizationOptions}
          selectedOrganization={selectedOrganization}
          onOrganizationChange={setSelectedOrganization}
          onOrganizationSearchChange={setOrganizationSearch}
          isLoadingOrganizations={isLoadingOrganizations}
          vehicleCategoryOptions={vehicleCategoryOptions}
          selectedVehicleCategories={selectedVehicleCategories}
          onVehicleCategoriesChange={setSelectedVehicleCategories}
          showVehicleCategories
        />
      ),
      activeCount: activeFiltersCount,
      activeFilters,
      onReset: resetFilters,
      onClearAll: resetFilters,
      isResetDisabled: !hasActiveFilters
    });
  }, [
    consignmentNumber,
    setConsignmentNumber,
    organizationOptions,
    selectedOrganization,
    setSelectedOrganization,
    setOrganizationSearch,
    isLoadingOrganizations,
    vehicleCategoryOptions,
    selectedVehicleCategories,
    setSelectedVehicleCategories,
    activeFilters,
    activeFiltersCount,
    hasActiveFilters,
    resetFilters,
    setFilterConfig,
    t
  ]);

  return <MappingPage filters={filters} />;
}
